import React, { useEffect, useMemo, useState } from 'react';
import {
    Badge, Box, Button, Chip, Collapse, Fab, FormControl, IconButton, MenuItem, Paper, Select, Tab, Tabs, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import DialpadIcon from '@mui/icons-material/Dialpad';
import CloseIcon from '@mui/icons-material/Close';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useSnackbar } from 'notistack';
import { tokens } from '../theme';
import { useVoice, VOICE_STATUS } from './VoiceContext';
import { errorMessage } from './voiceApi';
import { alsoText, formatDuration, formatPhone, isDialableNumber, personName, whomText } from './callFormat';
import { usePhoneLookup } from './usePhoneLookup';
import AlsoFileUnder from './AlsoFileUnder';
import { readSeenFailures, countUnseenFailures, markFailuresSeen } from './seenFailures';
import DialPad from './DialPad';
import ActivityCard from './ActivityCard';
import VoicePermissionsDialog from './VoicePermissionsDialog';
import ContactSearch from './ContactSearch';

const STATUS_TEXT = {
    [VOICE_STATUS.PREPARING]: 'Preparing…',
    [VOICE_STATUS.CONNECTING]: 'Connecting…',
    [VOICE_STATUS.RINGING]: 'Ringing…',
    [VOICE_STATUS.IN_CALL]: 'On call',
    [VOICE_STATUS.ENDED]: 'Call ended',
    [VOICE_STATUS.ERROR]: 'Call failed',
};

// Above MUI modals (1300) so a "Call" pressed inside the student modal shows the softphone on top.
const Z_INDEX = 1350;
// Stacked above the AI chat panel's button (bottom 24, 56 px). When the chat panel itself is open
// the two float in the same corner; the softphone paints on top and either can be closed.
const FAB_BOTTOM = 96;
const TAB_DIAL = 0;
const TAB_RECENT = 1;
const MY_RECENT_LIMIT = 20;

/**
 * Ticks once a second from `startedAt` and freezes at `endedAt` once the call is over; kept local so
 * only this component re-renders.
 */
const useElapsed = (startedAt, endedAt) => {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!startedAt) { setElapsed(0); return undefined; }
        const tick = () => setElapsed(Math.floor(((endedAt || Date.now()) - startedAt) / 1000));
        tick();
        if (endedAt) return undefined; // the call is over: show its final length, stop ticking
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [startedAt, endedAt]);
    return elapsed;
};

/**
 * The floating softphone (plan §6). Visible only to admins with the call permission. Placing a
 * call needs a click (browsers gate microphone/audio on a user gesture), so the Call button is the
 * only entry point; the "Call" icons on student cards just set the dial target.
 *
 * Two tabs: Dial (default; the current call lives here, and the tabs lock to it while a call is in
 * progress) and My recent calls (this admin's last 20 calls as cards, with a badge for failures not
 * yet seen — the same "seen" store the Calls bell uses, so opening either clears both).
 */
const SoftphoneWidget = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const voice = useVoice();
    const { enqueueSnackbar } = useSnackbar();
    const [showPad, setShowPad] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const [permOpen, setPermOpen] = useState(false);
    const [tab, setTab] = useState(TAB_DIAL);
    const [seen, setSeen] = useState(readSeenFailures);
    const elapsed = useElapsed(voice?.startedAt, voice?.endedAt);
    // Hooks run before the permission early-return below, so they read through `voice?.`.
    // Who has the number on file: while dialing (to name the number, or to offer siblings as "also file
    // under") and after a call attached to a student (to offer the siblings for that call).
    const dialing = Boolean(voice?.canCall) && !voice?.busy && voice?.status !== VOICE_STATUS.ENDED && voice?.status !== VOICE_STATUS.ERROR;
    const ended = voice?.status === VOICE_STATUS.ENDED;
    const lookup = usePhoneLookup(
        ended ? (voice?.callLog?.to_number || '') : (voice?.target?.to_number || ''),
        Boolean(voice?.canCall) && (dialing || (ended && Boolean(voice?.callLog?.student))),
    );

    const myCards = useMemo(() => {
        const myId = voice?.me?.admin?.id;
        return (voice?.feeds?.recent.cards || []).filter((c) => c.admin?.id === myId).slice(0, MY_RECENT_LIMIT);
    }, [voice?.feeds?.recent.cards, voice?.me?.admin?.id]);
    const unseenFailures = useMemo(() => countUnseenFailures(myCards, seen), [myCards, seen]);

    // The current call always lives on the Dial tab: a call in progress, or a number handed over by a
    // "Call" button on a student card, brings that tab forward.
    const isBusy = Boolean(voice?.busy);
    const targetNumber = voice?.target?.to_number;
    const targetStudentUuid = voice?.target?.student?.uuid;
    useEffect(() => { if (isBusy) setTab(TAB_DIAL); }, [isBusy]);
    useEffect(() => { if (targetNumber || targetStudentUuid) setTab(TAB_DIAL); }, [targetNumber, targetStudentUuid]);
    // Looking at the recent list marks its failures as seen (shared with the bell's badge).
    const recentShown = Boolean(voice?.widgetOpen) && tab === TAB_RECENT;
    useEffect(() => { if (recentShown) setSeen(markFailuresSeen(myCards, readSeenFailures())); }, [recentShown, myCards]);

    // Stay mounted through a call in progress even if the permission was just revoked, so the
    // admin can still hang up; the widget goes away once the call ends.
    if (!voice || (!voice.permitted && !voice.busy)) return null;

    const {
        status, busy, inCall, callLog, candidates, muted, error, canCall, callerIds, callerId, setCallerId,
        widgetOpen, setWidgetOpen, target, setTarget,
    } = voice;
    const number = target?.to_number || '';
    const canDial = canCall && !busy && isDialableNumber(number);
    // A Missed Calls row's call icon: kept while the number stays the same (picking who has it, clearing the pick).
    const missedContext = target?.missed_call_id ? { missed_call_id: target.missed_call_id, missed_label: target.missed_label } : {};
    // Typing or pressing a key edits the number by hand, so a student picked for the previous number
    // no longer applies; the lookup below says who has the new one.
    const setNumber = (next) => setTarget({ to_number: typeof next === 'function' ? next(number) : next });

    const press = (d) => {
        if (inCall) { voice.sendDigits(d); return; }
        setNumber((n) => n + d);
    };

    const call = async () => {
        try {
            await voice.placeCall({
                to_number: number,
                student_uuid: target?.student?.uuid,
                teacher_uuid: target?.teacher?.uuid,
                contact_label: target?.contact_label || undefined,
                also_student_uuids: target?.teacher ? undefined : (target?.also_students || []).map((s) => s.uuid),
                missed_call_id: target?.missed_call_id, // set only by a Missed Calls row's call icon
            });
        } catch { /* the error is shown from context */ }
    };

    /** After the call: replace the extra students the call is filed under. */
    const setAlsoStudents = async (list) => {
        try {
            await voice.attachCall(callLog.id, { also_student_uuids: list.map((s) => s.uuid) });
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        }
    };

    /** After the call: attach it to a student (with the label when a candidate gave one) or to a teacher. */
    const attach = async ({ student, teacher, contact_label }) => {
        try {
            const body = teacher ? { teacher_uuid: teacher.uuid } : contact_label ? { student_id: student.id, contact_label } : { student_uuid: student.uuid };
            await voice.attachCall(callLog.id, body);
            enqueueSnackbar(`Attached to ${personName(teacher || student)}`, { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        }
    };

    const retryCard = async (card) => {
        try {
            const { action } = await voice.retryCall(card.id);
            enqueueSnackbar(action === 'transcribe' ? 'Transcribing again…' : 'Fetching the recording again…', { variant: 'info' });
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        }
    };

    const startNew = () => { voice.reset(); setShowSearch(false); };

    const whom = callLog ? whomText(callLog) + alsoText(callLog)
        : target?.student ? whomText(target) + alsoText({ extra_students: target.also_students })
        : target?.teacher ? whomText(target)
        : formatPhone(number) || number;
    const onlyStudents = (list) => list.filter((m) => m.kind !== 'teacher'); // "also file under" is students-only

    return (
        <>
            {!widgetOpen && (
                <Tooltip title="Softphone" placement="left">
                    <Fab color={busy ? 'success' : 'primary'} onClick={() => setWidgetOpen(true)} sx={{ position: 'fixed', bottom: FAB_BOTTOM, right: 24, zIndex: Z_INDEX }}>
                        <PhoneIcon />
                    </Fab>
                </Tooltip>
            )}
            {widgetOpen && (
                <Paper
                    elevation={12}
                    sx={{
                        position: 'fixed', bottom: FAB_BOTTOM, right: 24, zIndex: Z_INDEX, width: 340, maxHeight: 'calc(100vh - 120px)',
                        borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: colors.primary[750],
                    }}
                >
                    {/* header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <PhoneIcon fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Softphone</Typography>
                        {callerIds.length > 1 ? (
                            <FormControl size="small" variant="standard" sx={{ minWidth: 130 }}>
                                <Select value={callerId || ''} onChange={(e) => setCallerId(e.target.value)} disabled={busy} disableUnderline sx={{ fontSize: 13 }}>
                                    {callerIds.map((id) => <MenuItem key={id} value={id} sx={{ fontSize: 13 }}>{formatPhone(id)}</MenuItem>)}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography variant="caption" color="text.secondary">{formatPhone(callerId)}</Typography>
                        )}
                        {voice.manager && (
                            <Tooltip title="Call permission"><IconButton size="small" onClick={() => setPermOpen(true)}><ManageAccountsOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        <IconButton size="small" onClick={() => setWidgetOpen(false)}><CloseIcon fontSize="small" /></IconButton>
                    </Box>

                    <Tabs
                        value={tab}
                        onChange={(e, v) => setTab(v)}
                        variant="fullWidth"
                        sx={{ minHeight: 40, borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                        <Tab label="Dial" sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }} />
                        <Tab
                            disabled={busy}
                            label={(
                                <Badge badgeContent={unseenFailures} color="error" max={9} sx={{ '& .MuiBadge-badge': { right: -10, top: 2 } }}>
                                    {`My recent calls${myCards.length ? ` (${myCards.length})` : ''}`}
                                </Badge>
                            )}
                            sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                        />
                    </Tabs>

                    {tab === TAB_RECENT && !busy ? (
                        <Box sx={{ p: 1.5, overflowY: 'auto' }}>
                            {myCards.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No calls yet.</Typography>
                            ) : (
                                myCards.map((c) => <ActivityCard key={c.id} card={c} dense onRetry={retryCard} />)
                            )}
                            {myCards.length >= MY_RECENT_LIMIT && (
                                <Typography variant="caption" color="text.secondary">Older calls are in the Calls bell and on each student's Calls tab.</Typography>
                            )}
                        </Box>
                    ) : (
                    <Box sx={{ p: 1.5, overflowY: 'auto' }}>
                        {!canCall && (
                            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                                {!voice.me?.configured ? 'Voice calling is not configured on the server.' : !voice.me?.enabled ? 'Voice calling is disabled in this environment.' : 'Voice calling is not available.'}
                            </Typography>
                        )}

                        {/* find a student or teacher to call */}
                        {!busy && status !== VOICE_STATUS.ENDED && canCall && (
                            <Box sx={{ mb: 1 }}>
                                {showSearch ? (
                                    <ContactSearch
                                        mode="call"
                                        autoFocus
                                        onPick={({ student, teacher, number: picked, contact_label }) => {
                                            const to_number = formatPhone(picked) || picked;
                                            setTarget(teacher ? { to_number, teacher, contact_label: 'teacher' } : { to_number, student, contact_label });
                                            setShowSearch(false);
                                        }}
                                    />
                                ) : (
                                    <Button size="small" startIcon={<PersonSearchOutlinedIcon />} onClick={() => setShowSearch(true)} sx={{ textTransform: 'none' }}>
                                        Find a student or teacher
                                    </Button>
                                )}
                            </Box>
                        )}

                        {/* from a Missed Calls row */}
                        {target?.missed_call_id && (
                            <Chip size="small" color="warning" variant="outlined" label={`부재중 ${target.missed_label || ''}`.trim()} sx={{ mb: 1 }} />
                        )}

                        {/* who */}
                        {(target?.student || target?.teacher || callLog) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <Chip size="small" color="primary" variant="outlined" label={whom} onDelete={!busy && !callLog ? () => setTarget({ to_number: number, ...missedContext }) : undefined} />
                                {(callLog?.student || target?.student)?.consultation_number && (
                                    <Typography variant="caption" color="text.secondary">#{(callLog?.student || target?.student).consultation_number}</Typography>
                                )}
                            </Box>
                        )}

                        {/* number / status */}
                        {busy || status === VOICE_STATUS.ENDED ? (
                            <Box sx={{ textAlign: 'center', my: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{whom}</Typography>
                                <Typography variant="body2" color={status === VOICE_STATUS.ENDED ? 'text.secondary' : 'success.main'}>
                                    {STATUS_TEXT[status]}{(inCall || (status === VOICE_STATUS.ENDED && elapsed > 0)) ? ` · ${formatDuration(elapsed)}` : ''}
                                </Typography>
                            </Box>
                        ) : (
                            <TextField
                                id="voice-dial-number"
                                value={number}
                                onChange={(e) => setNumber(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && canDial) call(); }}
                                placeholder="(201) 555-1234"
                                fullWidth
                                size="small"
                                inputProps={{ inputMode: 'tel', style: { fontSize: 20, textAlign: 'center', letterSpacing: 1 } }}
                                InputProps={{ endAdornment: number ? <IconButton size="small" onClick={() => setNumber((n) => n.slice(0, -1))}><BackspaceOutlinedIcon fontSize="small" /></IconButton> : null }}
                                sx={{ mb: 1 }}
                            />
                        )}

                        {/* siblings on the same number → "also file under" (before the call) */}
                        {dialing && target?.student && (
                            <AlsoFileUnder
                                primary={target.student}
                                selected={target.also_students || []}
                                suggestions={lookup.loading ? [] : onlyStudents(lookup.matches)}
                                onChange={(list) => setTarget({ ...target, also_students: list })}
                                allowSearch
                            />
                        )}
                        {/* after a call attached to a student: file it under the siblings too */}
                        {ended && callLog?.student && (
                            <AlsoFileUnder
                                primary={callLog.student}
                                selected={callLog.extra_students || []}
                                suggestions={lookup.loading ? [] : onlyStudents(lookup.matches)}
                                onChange={setAlsoStudents}
                                allowSearch
                            />
                        )}

                        {/* who has the typed number on file */}
                        {dialing && !target?.student && !target?.teacher && lookup.number && !lookup.loading && (
                            lookup.matches.length > 0 ? (
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        {lookup.matches.length > 1 ? 'Several people have this number. Pick one:' : 'On file as'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {lookup.matches.map((m) => (
                                            <Chip
                                                key={`${m.kind}-${m.uuid}`}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                icon={<PersonOutlineOutlinedIcon />}
                                                label={m.kind === 'teacher'
                                                    ? whomText({ teacher: m })
                                                    : `${whomText({ student: m, contact_label: m.contact_label })}${m.consultation_number ? ` · #${m.consultation_number}` : ''}`}
                                                onClick={() => setTarget({
                                                    ...missedContext,
                                                    ...(m.kind === 'teacher'
                                                        ? { to_number: number, teacher: m, contact_label: 'teacher' }
                                                        : { to_number: number, student: m, contact_label: m.contact_label }),
                                                })}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    No student or teacher has this number on file. The call will show under Unassigned.
                                </Typography>
                            )
                        )}

                        {error && <Typography variant="body2" color="error" sx={{ mb: 1, wordBreak: 'break-word' }}>{error}</Typography>}

                        {/* after the call: which student? */}
                        {status === VOICE_STATUS.ENDED && callLog && !callLog.student && !callLog.teacher && candidates.length > 1 && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>Several people have this number. Attach the call to:</Typography>
                                {candidates.map((c) => (
                                    <Button
                                        key={`${c.kind}-${c.id}`}
                                        size="small"
                                        fullWidth
                                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                        onClick={() => attach(c.kind === 'teacher' ? { teacher: c } : { student: c, contact_label: c.contact_label })}
                                    >
                                        {personName(c)} · {c.kind === 'teacher' ? 'teacher' : `#${c.consultation_number} (${c.contact_label})`}
                                    </Button>
                                ))}
                            </Box>
                        )}
                        {status === VOICE_STATUS.ENDED && callLog && !callLog.student && !callLog.teacher && candidates.length <= 1 && (
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>Attach this call to a student or teacher</Typography>
                                <ContactSearch mode="attach" onPick={({ student, teacher }) => attach({ student, teacher })} />
                            </Box>
                        )}

                        {/* keypad */}
                        {status !== VOICE_STATUS.ENDED && (
                            <>
                                {inCall && (
                                    <Button size="small" startIcon={<DialpadIcon />} onClick={() => setShowPad((v) => !v)} sx={{ mb: 0.5 }}>
                                        {showPad ? 'Hide keypad' : 'Keypad'}
                                    </Button>
                                )}
                                <Collapse in={showPad || !inCall}>
                                    <DialPad onPress={press} disabled={status === VOICE_STATUS.PREPARING || status === VOICE_STATUS.CONNECTING || status === VOICE_STATUS.RINGING} compact={inCall} />
                                </Collapse>
                            </>
                        )}

                        {/* actions */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                            {busy ? (
                                <>
                                    <Button variant="outlined" color="inherit" startIcon={muted ? <MicOffIcon /> : <MicIcon />} onClick={voice.toggleMute} disabled={!inCall} sx={{ flex: 1 }}>
                                        {muted ? 'Unmute' : 'Mute'}
                                    </Button>
                                    <Button variant="contained" color="error" startIcon={<CallEndIcon />} onClick={voice.hangUp} sx={{ flex: 1 }}>
                                        Hang up
                                    </Button>
                                </>
                            ) : status === VOICE_STATUS.ENDED || status === VOICE_STATUS.ERROR ? (
                                <Button variant="contained" onClick={startNew} sx={{ flex: 1 }}>New call</Button>
                            ) : (
                                <Button variant="contained" color="success" startIcon={<PhoneIcon />} onClick={call} disabled={!canDial} sx={{ flex: 1 }}>
                                    Call
                                </Button>
                            )}
                        </Box>
                        {status === VOICE_STATUS.ENDED && callLog && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                The recording and transcript will appear under Calls once they are processed.
                            </Typography>
                        )}
                    </Box>
                    )}
                </Paper>
            )}
            {voice.manager && <VoicePermissionsDialog open={permOpen} onClose={() => setPermOpen(false)} />}
        </>
    );
};

export default SoftphoneWidget;
